import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, useField, useFormikContext } from 'formik';
import classNames from 'classnames';

import GenericShippingMethod from './GenericShippingMethod';
import PickupIcon from 'components/Common/Icons/Delivery/Pickup';
import ModalColissimo from 'components/Modals/ModalColissimo/ModalColissimo';
import Button from 'components/UI/Buttons/Button/Button';
import { useModal } from 'hooks/useModal';
import { RelayPoint } from 'types/Cart';
import { MethodProps, ShippingFormType } from 'types/ShippingFormType';

import styles from './ShippingMethod.module.scss';

export default function Relay({
  isSelected,
  method: { methodCode, estimateDeliveryDates },
  cart,
  setShippingInformation,
}: MethodProps): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const { setValues, values } = useFormikContext<ShippingFormType>();
  const [{ value: methodSelected }] = useField('shippingMethod');
  const [
    { value: relayPointSelected },
    ,
    { setValue: setRelayPoint },
  ] = useField('relayPoint');

  const { open, openModal, closeModal } = useModal();
  const [colissimoReady, setColissimoReady] = useState(false);

  function onChoosePickup() {
    openModal();
  }

  function onPickupSelect(point: RelayPoint) {
    closeModal();

    setValues(prev => ({
      ...prev,
      shippingMethod: methodCode,
      relayPoint: point,
      addressType: '',
    }));

    setShippingInformation({
      shippingMethod: methodCode,
      relayPoint: point,
    });
  }

  useEffect(() => {
    if (methodSelected === methodCode) {
      setShippingInformation({
        shippingMethod: methodCode,
        relayPoint: relayPointSelected,
      });
    }
  }, [methodSelected]);

  useEffect(() => {
    // Use other effect to prevent call updateShippingMethod multiple times because relayPointSelected is an object.
    if (methodSelected !== methodCode && relayPointSelected) {
      setRelayPoint(undefined, false);
    }
  }, [methodSelected, relayPointSelected]);

  return (
    <GenericShippingMethod
      value={methodCode}
      methodIcon={<PickupIcon style={{ color: '#16367c', fontSize: '32px' }} />}
      methodLabel={
        <>
          <span className={styles.methodName}>
            {t('SHIPPING_PICKUP_METHOD_NAME')}
          </span>
          <span className={styles.methodNameExplain}>
            {t('SHIPPING_PICKUP_METHOD_NAME_EXPLAIN')}
          </span>
        </>
      }
      estimateDeliveryDates={estimateDeliveryDates}
      price={cart.shippingFees}
      freeFrom={
        cart.shippingFees
          ? cart.freeShippingThresholdInformation?.amount
          : undefined
      }
      classes={{
        methodContent: classNames(styles.relayContent, {
          [styles.isSelected]: isSelected,
        }),
      }}
    >
      {isSelected && (
        <>
          {values.relayPoint && <RelayAddress address={values.relayPoint} />}
          <Button
            type={'button'}
            onClick={onChoosePickup}
            disabled={!colissimoReady}
            model={'primary'}
          >
            {t('SHIPPING_PICKUP_METHOD_CHOOSE_BUTTON')}
          </Button>
          <ErrorMessage name={'relayPoint'} />
        </>
      )}
      <ModalColissimo
        isOpen={open}
        closeModal={closeModal}
        onSelect={onPickupSelect}
        widgetReady={() => setColissimoReady(true)}
      />
    </GenericShippingMethod>
  );
}

function RelayAddress({ address }: { address: RelayPoint }): JSX.Element {
  const { t } = useTranslation(['checkout']);

  return (
    <div className={styles.relayAddress}>
      <div className={styles.yourReplayPoint}>
        {t('SHIPPING_PICKUP_METHOD_YOUR_RELAY_POINT')}
      </div>
      <div className={styles.dataTextRelayPoint}>
        <div>{address?.nom}</div>
        <div>{address?.adresse1}</div>
        <div>{`${address?.codePostal} ${address?.localite}`}</div>
      </div>
    </div>
  );
}
